// INVARIANT(scriptera): СЦЕНА-ПРИМЕР НАРИСОВАНА В ДВУХ МЕСТАХ — веб (apps/web: components/auth/app-onboarding.tsx,
// components/auth/onboarding.css, components/mock-phone.{tsx,css}, app/[locale]/wizard-scene/[scene]/*,
// lib/scene-script.ts) и iOS (scriptera-app: ios/App/App/Kit/Scenes/*). Правишь вид, шаг, текст-раскладку, тайминг,
// цвет или размер — правь ОБА места тем же днём и сверяй кадры склейкой «веб | iOS» (~/ops/native-scenes). Причина:
// владелец 26.10.2026 — сцены в приложении нативные, «пиксель в пиксель». Канон: ~/projects/NATIVE-SCENES-TZ.md,
// docs/HOW-IT-WORKS.md §«Сцены-примеры».
//
// СЦЕНАРИЙ СЦЕНЫ — ОДНА ИСТИНА ДЛЯ ВЕБА И ПРИЛОЖЕНИЯ (ТЗ NATIVE-SCENES §3.1–§3.2, владелец 26.10.2026: «все 6 сцен
// сделать нативными»). Здесь живут:
//   • `sceneDefs(t)` — определения всех сцен (бывшее тело `useScenes`, app-onboarding.tsx — теперь обёртка над ним);
//   • `sceneDefFor` — копия сцены под нишу, лицо и фразы бота (бывшая `sceneDefOf` страницы /wizard-scene);
//   • `buildSceneScript` — та же копия, сведённая в данные `SceneScript` для родного рисовальщика iOS;
//   • `buildScenePack` — пакет всех сцен языка (ручка GET /api/app/scenes).
// Веб рисует `sceneDefFor(...)` компонентом `Scene`, приложение — `buildSceneScript(...)` своими вьюхами: обе стороны
// получают ОДНО И ТО ЖЕ из одной функции. Тексты сцен — блок словаря `auth.onboarding.*` (messages/{ru,en}.json): это
// тоже часть сцены, правка текста меняет снимок пакета (scene-script.test.ts) — сигнал «правь второе место».
// Формат `SceneScript` растёт ТОЛЬКО добавлением полей (приложение пропускает незнакомые); смысл поля не меняется.
//
// Метки 'use client' здесь нет нарочно: модуль зовут сервер (страница сцены, ручка пакета, страницы мастера) и
// клиент (Scene, тур, лендинг). Хуков и разметки нет.
import { assetUrl, ASSET_VERSION } from '@/lib/asset-url';
import { nicheScene, type PersonalProfile } from '@/lib/niche-scene';
import {
  SCENE_AVA,
  SCENE_BOT_AVA,
  SCENE_NICHE_POST,
  SCENE_POST_IMG,
} from '@/lib/scene-assets';
import type { SceneTexts, WizardScene } from '@/lib/wizard-scene-texts';
import { hasOwnLetters, withSeries, withoutSeries, type SceneSeries } from '@/lib/scene-series';
import type { QsRenderCtx } from '@/lib/quick-start/render';

const AVA = SCENE_AVA;

// ── Определение сцены (тот же вид, что был в app-onboarding.tsx) ─────────────────────────────────────────────────

export type SceneStep =
  | { k: 'post'; text: string }
  | { k: 'draft'; text: string }
  | { k: 'bot'; text: string }
  /** `above` (§10.12 п.4) — неактивная кнопка-ссылка над кнопкой шага (Телеграм: «Канал» над «Готово»).
   *  `auto` (QUICK-START §11.20, владелец 29.10: «в цепочке все эти сообщения, хоть и есть кнопки, — не надо ждать их
   *  нажатия; только последнее, „Купить“, — просить нажатие»): кнопка под сообщением серии стоит, но сцена её НЕ ждёт —
   *  следующее сообщение идёт по таймингу бота. Аддитивно: старая сборка ждёт нажатия, как раньше. */
  | { k: 'chip'; text: string; above?: string; auto?: true }
  | { k: 'echo'; text: string }
  | { k: 'note'; text: string }
  | { k: 'start' }
  | { k: 'menu'; items: { name: string; price: string }[] }
  | { k: 'echoPick' }
  | { k: 'bill' }
  | { k: 'result'; text: string; big?: boolean }
  | { k: 'file'; name: string; size: string }
  | { k: 'product'; name: string; price: string }
  | { k: 'jump'; text: string; to?: 'tg' }
  | { k: 'day'; text: string };

export interface SceneDef {
  /** Ключ сцены — блок текстов auth.onboarding.<k>; им же анкеты выбирают сцену (demoScene). */
  k: string;
  /** Спрятана из тура и лендинга (владелец 31.08.2026: виджет с оплатой убрать), но жива
   *  для мастеров (/wizard-scene, шаг «Знакомство» анкеты). */
  hidden?: boolean;
  /** Чей экран изображает сцена: директ/комментарии Инстаграма или переписка Телеграма. */
  chat: 'ig' | 'tg';
  /** Диалог с НАШИМ аккаунтом scripteraai (первая сцена): знак-логотип вместо серых
   *  кружков, подсказка со стрелкой и блеском, заметный самолёт (владелец 27.08). */
  own?: boolean;
  /** Имя в шапке диалога нашего аккаунта. */
  name?: string;
  /** Чей аватар у собеседника: фото-иллюстрация Дарьи (стоковая персона, ресурс `ava.marina`) (вторая сцена). */
  ava?: 'marina';
  /** Сцена начинается телеграмной заставкой бота с кнопкой «Начать» (третья). */
  tgStart?: boolean;
  /** Телеграм: аватар бота в шапке и на заставке; пусто — аватар третьей сцены. */
  tgAva?: string;
  /** QUICK-START §10.2 п.2: имя в шапке Телеграма у сцены С ПЕРЕХОДОМ из директа (g2) — имя бота человека; пусто — `name`.
   *  У сцены Телеграма целиком (s4, s5) имя бота стоит в `name`. */
  tgName?: string;
  /** Телеграм: карточка заставки «что умеет бот» и подпись кнопки старта; пусто — тексты
   *  третьей сцены (совместимость). */
  tgAbout?: { h: string; p: string; btn: string };
  /** Сцена живёт внутри нарисованного телефона (сцены 4-5 стоят в воздухе). */
  phone: boolean;
  /** Сюжет начинается в комментариях под постом (сцена 2). */
  comments?: boolean;
  nick?: string;
  botWho?: string;
  /** ПЕРСОНАЛИЗАЦИЯ (портрет клиента, бэклог 31.08): лицо владельца бота, картинка его
   *  публикации и имена его аудитории под постом. Пусто — сцена рисуется как рисовалась,
   *  до пикселя. Заполняет их КОПИЯ дефа снаружи (lib/niche-scene.ts), сама сцена о
   *  портрете ничего не знает. */
  selfAva?: string;
  postImg?: string;
  /** Лица аудитории под постом: имя и (О5.7) публичный адрес аватарки комментатора
   *  (`/api/scene-face/...`); фото нет — фирменный кружок (личный/приватный аккаунт). */
  selfFaces?: { name?: string | null; avatar?: string | null }[];
  steps: SceneStep[];
  /** QUICK-START §8.22: набор серии и отрезок шагов, который при показе заменяется пятью случайными сообщениями
   *  (lib/scene-series.ts `expandSeries`). Нет — шаги играются как есть. */
  series?: SceneSeries;
}

/** Перевод блока `auth.onboarding` (подходят и `useTranslations`, и `getTranslations`/`createTranslator`). */
export type SceneT = (key: string, values?: Record<string, string>) => string;

/** ВСЕ сцены примеров; порядок массива = порядок тура (бэклог «Примеры работ 31.08»:
 *  слово в комментарии → подарок ИГ → подарок ТГ → цепочка рассылок), спрятанные — в
 *  хвосте. Реплики s1–s3 СНЯТЫ с наших шаблонов bot_templates (приказ владельца 28.08:
 *  диалоги не сочиняются) — меняется шаблон, правим и ключи auth.onboarding. Тексты
 *  подарка ТГ (s4) — по мотивам шаблона «Проверка подписки», цепочки (s5) — черновик
 *  исполнителя 01.09 (шаблона-источника у цепочки пока нет), оба на приёмке владельца. */
export function sceneDefs(t: SceneT, locale?: 'ru' | 'en'): SceneDef[] {
  // §8.22: с языком — сцены с цепочкой (g2, s5) несут набор серии; без языка — как были.
  const defs = sceneDefsBase(t);
  return locale ? defs.map((d) => withSeries(d, locale)) : defs;
}

function sceneDefsBase(t: SceneT): SceneDef[] {
  return [
    {
      // ЦЕЛЬ «ПОДПИСКИ» (владелец 23.10.2026, ТЗ ~/projects/GOAL-SCENES-HELP-TZ.md): копия «Слова в
      // комментарии» + шаг подписки — пост → комментарий → публичный ответ → директ: «подпишитесь и
      // нажмите кнопку» → «Я подписался» → бот проверил подписку → отдал обещанное. Тексты формальные,
      // на «вы». Показывается на лендинге (блок «Какая у вас цель?») и в туре; мастера её не берут.
      k: 'g1',
      chat: 'ig',
      phone: true,
      comments: true,
      own: true,
      name: t('s2.name'),
      ava: 'marina',
      nick: t('s2.who'),
      steps: [
        { k: 'post', text: t('g1.post') },
        { k: 'draft', text: t('g1.comment') },
        { k: 'bot', text: t('g1.pub') },
        { k: 'note', text: t('s2.note') },
        { k: 'jump', text: t('s2.dm') },
        { k: 'bot', text: t('g1.reply') },
        { k: 'chip', text: t('g1.chip') },
        { k: 'echo', text: t('g1.chip') },
        { k: 'result', text: t('g1.ok'), big: true },
        { k: 'bot', text: t('g1.give') },
        { k: 'file', name: t('g1.file'), size: t('g1.size') },
      ],
    },
    {
      // ЦЕЛЬ «ПРОДАЖИ» (там же): пост → комментарий → публичный ответ → директ: обещанное ждёт в
      // Телеграме, кнопка → переход в Телеграм (jump to: 'tg') → бот отдаёт файл → серия сообщений по
      // дням с кнопками → «Купить программу». Подписку не проверяет.
      k: 'g2',
      chat: 'ig',
      phone: true,
      comments: true,
      own: true,
      name: t('s2.name'),
      ava: 'marina',
      nick: t('s2.who'),
      botWho: t('s5.sub'),
      tgAva: AVA.marina,
      steps: [
        { k: 'post', text: t('g2.post') },
        { k: 'draft', text: t('g2.comment') },
        { k: 'bot', text: t('g2.pub') },
        { k: 'note', text: t('s2.note') },
        { k: 'jump', text: t('s2.dm') },
        { k: 'bot', text: t('g2.reply') },
        { k: 'chip', text: t('g2.chip') },
        { k: 'jump', text: t('g2.tg'), to: 'tg' },
        // QUICK-START §10.12 п.3 (владелец 29.10: «нажимаешь „Забрать“ — открывается Телеграм, и там первым сообщением
        // идёт „/start“, сразу отправляется»): заставки «Начать» нет — в ленте сразу «/start» от человека, затем бот.
        { k: 'echo', text: '/start' },
        { k: 'bot', text: t('g2.give') },
        { k: 'file', name: t('g2.file'), size: t('g2.size') },
        { k: 'day', text: t('g2.d1') },
        { k: 'bot', text: t('g2.m1') },
        { k: 'chip', text: t('g2.b1'), auto: true },
        { k: 'day', text: t('g2.d2') },
        { k: 'bot', text: t('g2.m2') },
        { k: 'chip', text: t('g2.b2'), auto: true },
        { k: 'day', text: t('g2.d3') },
        { k: 'bot', text: t('g2.m3') },
        { k: 'chip', text: t('g2.buy') },
        { k: 'echo', text: t('g2.buy') },
        { k: 'result', text: t('g2.ok'), big: true },
      ],
    },
    {
      // ЧЕТЫРЕ СЦЕНЫ ИНСТРУМЕНТОВ (s2, s1, s4, s5) — с 23.10.2026 СПРЯТАНЫ из тура и лендинга
      // (владелец: «на лендинге мы их показывать не будем вообще и в мобильном приложении»), но живы
      // для мастеров: «Пример переписки» в анкете и /wizard-scene берут их из полного списка.
      k: 's2',
      hidden: true,
      chat: 'ig',
      phone: true,
      comments: true,
      own: true,
      name: t('s2.name'),
      ava: 'marina',
      nick: t('s2.who'),
      // Пост со схемами и чужими комментариями; после плашки — дёргающееся «Новое
      // сообщение», тап открывает личку: «жми Получить» и файл (владелец 27.08).
      steps: [
        { k: 'post', text: t('s2.post') },
        { k: 'draft', text: t('s2.comment') },
        { k: 'bot', text: t('s2.pub') },
        { k: 'result', text: t('s2.ok') },
        { k: 'note', text: t('s2.note') },
        { k: 'jump', text: t('s2.dm') },
        { k: 'bot', text: t('s2.reply') },
        { k: 'chip', text: t('s2.chip') },
        // Нажатие кнопки публикуется исходящим сообщением (владелец 28.08).
        { k: 'echo', text: t('s2.chip') },
        { k: 'bot', text: t('s2.give') },
        { k: 'file', name: t('s2.file'), size: t('s2.size') },
      ],
    },
    {
      k: 's1',
      hidden: true,
      chat: 'ig',
      phone: true,
      own: true,
      // Наш аккаунт: имя и ник — бренд, не переводятся.
      name: 'Scriptera AI',
      nick: 'scripteraai',
      steps: [
        { k: 'draft', text: t('s1.ask') },
        { k: 'bot', text: t('s1.reply') },
        { k: 'chip', text: t('s1.chip') },
        // Нажатие кнопки в настоящем директе публикуется исходящим сообщением.
        { k: 'echo', text: t('s1.chip') },
        { k: 'result', text: t('s1.ok') },
        { k: 'bot', text: t('s1.give') },
        { k: 'file', name: t('s1.file'), size: t('s1.size') },
      ],
    },
    {
      // Подарок за подписку в Телеграме — тот же сюжет, что подарок ИГ, в оформлении
      // Телеграма и от бота Дарьи (бэклог «Примеры работ 31.08»).
      k: 's4',
      hidden: true,
      chat: 'tg',
      phone: true,
      name: t('s2.name'),
      botWho: t('s4.sub'),
      tgAva: AVA.marina,
      tgAbout: { h: t('s4.aboutH'), p: t('s4.about'), btn: t('s4.startBtn') },
      // §10.12 п.3: без заставки — первым в ленте отправленное «/start». п.4: просьба подписаться — с кнопкой «Канал»
      // над «Готово» (ссылка на канал; в сцене не нажимается, как у настоящего бота ведёт наружу).
      steps: [
        { k: 'echo', text: '/start' },
        { k: 'bot', text: t('s4.reply') },
        { k: 'chip', text: t('s4.chip'), above: t('s4.channel') },
        // Нажатие кнопки публикуется исходящим сообщением (как в подарке ИГ).
        { k: 'echo', text: t('s4.chip') },
        { k: 'result', text: t('s4.ok') },
        { k: 'bot', text: t('s4.give') },
        { k: 'file', name: t('s4.file'), size: t('s4.size') },
      ],
    },
    {
      // Цепочка рассылок: человек прошёл воронку (получил подарок) — дальше бот сам
      // ведёт его серией сообщений по дням до заявки (владелец 01.09: «нарисуй»).
      k: 's5',
      hidden: true,
      chat: 'tg',
      phone: true,
      name: t('s2.name'),
      botWho: t('s5.sub'),
      tgAva: AVA.marina,
      // §10.12 п.3: цепочка в Телеграме начинается с отправленного «/start» — без заставки.
      steps: [
        // §11.20 п.1 (владелец 29.10: «первое сообщение не должно быть в цепочке»): сообщения с подарком нет — оно
        // только в переходе из цепочки-из-одного-сообщения (g2). «/start» → сразу серия.
        { k: 'echo', text: '/start' },
        { k: 'day', text: t('s5.d1') },
        { k: 'bot', text: t('s5.m1') },
        { k: 'day', text: t('s5.d2') },
        { k: 'bot', text: t('s5.m2') },
        { k: 'day', text: t('s5.d3') },
        { k: 'bot', text: t('s5.m3') },
        { k: 'chip', text: t('s5.chip') },
        { k: 'echo', text: t('s5.chip') },
        { k: 'result', text: t('s5.ok') },
      ],
    },
    {
      // СЦЕНЫ ДЛЯ РОЛИКА (владелец 22.09.2026, три показа бота из текста ролика; спрятаны
      // из тура и лендинга, живут для мастера /wizard-scene/s6..s8; лицо и слова
      // клиента в них НЕ подставляются — стоковые персоны под тему, род совпадает).
      // s6 — «Вы наверняка встречали такое»: слово в комментарии → директ → просят
      // подписаться → ссылка в Телеграм, где лежит обещанное. Дарья Фитнес.
      k: 's6',
      hidden: true,
      chat: 'ig',
      phone: true,
      comments: true,
      own: true,
      // Имена ролика (владелец 22.09): Дарья Фитнес / darya.fitness, комментатор —
      // Анастасия (то же лицо, что в s8: anastasia.english).
      name: t('s6.name'),
      ava: 'marina',
      nick: t('s6.who'),
      steps: [
        { k: 'post', text: t('s2.post') },
        { k: 'draft', text: t('s2.comment') },
        { k: 'bot', text: t('s2.pub') },
        { k: 'note', text: t('s2.note') },
        { k: 'jump', text: t('s2.dm') },
        { k: 'bot', text: t('s6.reply') },
        { k: 'chip', text: t('s6.sub') },
        { k: 'echo', text: t('s6.sub') },
        { k: 'bot', text: t('s6.link') },
        { k: 'chip', text: t('s6.chip') },
      ],
    },
    {
      // s7 — простой бот: просит подписаться и отдаёт обещанное прямо в Инстаграме.
      // Максим, инвестиции (мужской род).
      k: 's7',
      hidden: true,
      chat: 'ig',
      phone: true,
      own: true,
      name: t('s7.name'),
      nick: t('s7.who'),
      selfAva: AVA.maks,
      steps: [
        { k: 'draft', text: t('s7.ask') },
        { k: 'bot', text: t('s7.reply') },
        { k: 'chip', text: t('s7.chip') },
        { k: 'echo', text: t('s7.chip') },
        { k: 'result', text: t('s7.ok') },
        { k: 'bot', text: t('s7.give') },
        { k: 'file', name: t('s7.file'), size: t('s7.size') },
      ],
    },
    {
      // s8 — продвинутый бот: из Инстаграма в Телеграм, там выдача, дальше цепочка по
      // дням с кнопкой в каждом письме, в конце «Купить». Анастасия, английский язык.
      // Переход в Телеграм внутри сцены — шаг `jump` с `to: 'tg'`.
      k: 's8',
      hidden: true,
      chat: 'ig',
      phone: true,
      own: true,
      name: t('s8.name'),
      nick: t('s8.who'),
      selfAva: AVA.dasha,
      botWho: t('s5.sub'),
      tgAva: AVA.dasha,
      steps: [
        { k: 'bot', text: t('s8.reply') },
        { k: 'chip', text: t('s8.chip') },
        { k: 'jump', text: t('s8.tg'), to: 'tg' },
        { k: 'bot', text: t('s8.give') },
        { k: 'file', name: t('s8.file'), size: t('s8.size') },
        { k: 'day', text: t('s5.d1') },
        { k: 'bot', text: t('s8.m1') },
        { k: 'chip', text: t('s8.b1') },
        { k: 'day', text: t('s5.d2') },
        { k: 'bot', text: t('s8.m2') },
        { k: 'chip', text: t('s8.b2') },
        { k: 'day', text: t('s5.d3') },
        { k: 'bot', text: t('s8.m3') },
        { k: 'chip', text: t('s8.buy') },
      ],
    },
    {
      // «Доводит до оплаты» — С ЭКРАНОВ УБРАНА (владелец 31.08.2026: виджет с оплатой
      // и кнопками скрыть), но жива: её показывают мастера ТГ (/wizard-scene/s3).
      k: 's3',
      hidden: true,
      chat: 'tg',
      phone: true,
      tgStart: true,
      // Имя, подпись и заставка — те же строки, на которые Scene падала без них; заданы
      // явно, чтобы копия сцены под клиента (personalizeScene) могла их ПОДМЕНИТЬ: иначе
      // у клиента в телефоне оставался бренд «Beyond English» (О5.9, попутная находка Б).
      name: t('s3.botName'),
      botWho: t('s3.botSub'),
      tgAbout: { h: t('s3.aboutH'), p: t('s3.about'), btn: t('s3.startBtn') },
      // Заставка бота → /start → выбор из трёх форматов курса кнопками → счёт по
      // выбранному со ссылкой на оплату (владелец 28.08). Цены — товара КЛИЕНТА.
      steps: [
        { k: 'start' },
        { k: 'echo', text: '/start' },
        { k: 'bot', text: t('s3.hello') },
        {
          k: 'menu',
          items: [
            { name: t('s3.m1n'), price: t('s3.m1p') },
            { name: t('s3.m2n'), price: t('s3.m2p') },
            { name: t('s3.m3n'), price: t('s3.m3p') },
          ],
        },
        // Выбор публикуется сообщением: «название · цена» (владелец 28.08).
        { k: 'echoPick' },
        { k: 'bill' },
        { k: 'chip', text: t('s3.pay') },
        { k: 'result', text: t('s3.ok') },
        { k: 'bot', text: t('s3.done') },
        { k: 'file', name: t('s3.file'), size: t('s3.size') },
      ],
    },
  ];
}

// ── Копия сцены под нишу, лицо и фразы бота (одна для страницы /wizard-scene и для сценария приложения) ─────────

/**
 * Копия сцены с фразами этого бота: сама сцена не меняется (см. wizard-scene-texts).
 * Дальше — лицо и слова ниши клиента ТЕМ ЖЕ personalizeScene, что в веб-мастере
 * (владелец 03.09.2026: пример в приложении показывал чужой аккаунт и чужую нишу).
 * Бывшая `sceneDefOf` страницы /wizard-scene (scene-view.tsx) — вынесена сюда без правок логики, чтобы веб и
 * сценарий приложения строились одной функцией.
 */
export function sceneDefFor(o: {
  base: SceneDef | null;
  key: WizardScene;
  /** Слова ниши (nichePhrases); пусто — слова сцены. */
  nicheTexts?: SceneTexts | null;
  /** Ключ ниши — фото поста и автор поста ниши. */
  nicheKey?: string | null;
  /** Фразы собранного бота (правка анкеты) — поверх слов ниши. */
  texts?: SceneTexts | null;
  /** Лицо клиента; пусто — лицо сцены. */
  profile?: PersonalProfile | null;
  locale: string;
  /** QUICK-START §9.3 п.1: у сцен цели (g1/g2) — лицо человека (ступень «persona» лестницы); нет — как у тура, без лица. */
  face?: boolean;
  /** QUICK-START §8.22 + §9.3: оффер для сообщений цепочки (набор серии под продукт/результат человека). */
  seriesCtx?: Partial<QsRenderCtx>;
}): SceneDef | null {
  const out = sceneDefForBase(o);
  // QUICK-START §11.20 / §8.22: свои письма бота (`msg1`–`msg4`) — сцена без выборки набора: места писем у s5 теперь
  // внутри отрезка серии (письма подарка в ней нет), и выборка набора перекрыла бы их.
  if (out && out.series && hasOwnLetters(o.texts)) return withoutSeries(out);
  if (!out || !o.seriesCtx || !out.series) return out;
  return withSeries(withoutSeries(out), o.locale === 'en' ? 'en' : 'ru', o.seriesCtx);
}

function sceneDefForBase(o: Parameters<typeof sceneDefFor>[0]): SceneDef | null {
  const { base, key: scene, locale } = o;
  const nicheTexts = o.nicheTexts ?? null;
  const nicheKey = o.nicheKey ?? null;
  const profile = o.profile ?? null;
  if (!base) return null;
  // Сцены для ролика (s6–s8, владелец 22.09.2026) — стоковые персоны под тему: лицо, ник и
  // слова клиента в них не подставляются.
  // Сцены цели (g1/g2, тур до входа): слова ниши (жребий сервера или своя) — подставляются, лица нет.
  if (scene === 's6' || scene === 's7' || scene === 's8') return base;
  if (scene === 'g1' || scene === 'g2') return nicheScene(base, nicheTexts, nicheKey, o.face ? profile : null, locale);
  // Слова одним набором: слова ниши как основа, свои фразы бота (правка) ПОВЕРХ них
  // (человек их сам написал). В нём же едет `about` — описание бота на заставке (s4),
  // которое personalizeScene подменяет полем дефа. Пусто — сцена как есть.
  const merged = { ...(nicheTexts ?? {}), ...(o.texts ?? {}) };
  const has = Object.keys(merged).length > 0;
  return nicheScene(base, has ? merged : null, nicheKey, profile, locale);
}

// ── СЦЕНАРИЙ ДЛЯ ПРИЛОЖЕНИЯ (§3.1) ──────────────────────────────────────────────────────────────────────────────

/** Формат сценария и пакета: растёт только добавлением. 2 — §8.22: у сценария необязательное `series`. */
export const SCENE_SCRIPT_FORMAT = 2;

/** Шесть сцен, которые приложение рисует нативно (§2.1): тур — g1/g2, мастера — s1/s2/s4/s5. */
export const NATIVE_SCENES = ['g1', 'g2', 's1', 's2', 's4', 's5'] as const;
export type NativeSceneKey = (typeof NATIVE_SCENES)[number];
export const TOUR_SCENES = ['g1', 'g2'] as const;

/**
 * Картинка в сценарии:
 *  • строка-id — ресурс пакета (`assets[].id`, лежит в сборке приложения);
 *  • `""` — фирменный кружок (аватарки нет — никогда чужое лицо, Д5);
 *  • `null` — знак Scriptera (BrandBolt; только `faces.self` у сцены нашего аккаунта);
 *  • объект `remote` — картинка человека (аватарка аккаунта, его публикация, лица аудитории): адрес полный, если
 *    сборщику дан `origin`, иначе путь сайта; размеры — когда сервер их знает (без них — размер места в сцене).
 */
export type SceneImageRef = string | null | { url: string; remote: true; w?: number; h?: number };

/** Слова «вокруг» переписки на языке сцены (подсказки, поля, шапки, запасные имена). */
export interface SceneUi {
  hintSend: string;
  hintTap: string;
  /** С подстановкой `{btn}` — имя кнопки (подставляет рисовальщик). */
  hintTapNamed: string;
  hintWay: string;
  again: string;
  igField: string;
  cmField: string;
  tgField: string;
  cmHeader: string;
  igToday: string;
  profStart: string;
  tgBotName: string;
  tgBotSub: string;
  tgStartBtn: string;
  /** Пометка «Пример переписки» на месте острова (W31, владелец 27.10.2026). Добавлено 27.10 — старая сборка не читает. */
  exampleBadge?: string;
}

export interface SceneScript {
  format: number;
  key: string;
  locale: 'ru' | 'en';
  /** С какого экрана начинается сцена. */
  chat: 'ig' | 'tg';
  own: boolean;
  comments: boolean;
  tgStart: boolean;
  /** Имя в шапке директа и Телеграма (у сцены без имени — ник для директа, `ui.tgBotName` для Телеграма). */
  name: string;
  /** QUICK-START §10.2 п.2 (добавлено): имя в шапке Телеграма после перехода из директа — имя бота; нет поля — `name`. */
  tgName?: string;
  nick: string | null;
  /** Подпись под именем в Телеграме; null — `ui.tgBotSub`. */
  botWho: string | null;
  faces: {
    self: SceneImageRef;
    tg: SceneImageRef;
    post: SceneImageRef;
    commenter: { name: string; img: SceneImageRef };
  };
  tgAbout: { h: string; p: string; btn: string } | null;
  steps: SceneStep[];
  ui: SceneUi;
  /** §8.22 (формат 2): набор серии (`series` дефа); нет — шаги как есть. Старая сборка поле не читает. */
  series?: SceneSeries;
}

export interface SceneAsset {
  id: string;
  /** Путь сайта с версией (`?v=`), как его просит веб. */
  url: string;
  w: number;
  h: number;
}

/** Числа такта (§7.2–§7.3, app-onboarding.tsx `Scene`) — данными, чтобы правка не требовала сборки приложения. */
export const SCENE_TIMING = {
  post: 600,
  jump: 550,
  day: 550,
  file: 600,
  echo: 600,
  product: 600,
  botTyping: 900,
  bot: 1450,
  result: 750,
  resultBig: 1400,
  plateLife: 2950,
  plateLifeBig: 4350,
  tapNext: 350,
  bill: 650,
  typeChar: 50,
  typeDone: 150,
  sendNext: 500,
  replayStart: 400,
  wiggle: 620,
  wiggleEvery: 6000,
  feedSlide: 400,
  fadeIn: 500,
} as const;
export type SceneTimingName = keyof typeof SCENE_TIMING;

/** Картинки сцен, которые лежат в сборке приложения (§3.3): лица, пост и бот по умолчанию, посты ниш. */
const STOCK_ASSET_FILES: ReadonlyArray<{ id: string; path: string; w: number; h: number }> = [
  { id: 'ava.marina', path: '/landing2/ava-daria.png', w: 876, h: 848 },
  { id: 'ava.dasha', path: '/landing2/ava-anastasi.png', w: 640, h: 640 },
  { id: 'ava.maks', path: '/landing2/ava-maksim.jpg', w: 640, h: 640 },
  { id: 'post.stock', path: '/onboarding-post.jpg', w: 1080, h: 580 },
  { id: 'bot.stock', path: '/onboarding-bot.png', w: 256, h: 256 },
  ...Object.entries(SCENE_NICHE_POST).map(([k, path]) => ({ id: `post.${k}`, path, w: 1080, h: 580 })),
];

/** Ресурсы пакета (адрес с версией сборки). */
export function sceneAssets(): SceneAsset[] {
  return STOCK_ASSET_FILES.map((a) => ({ id: a.id, url: assetUrl(a.path), w: a.w, h: a.h }));
}

/** Путь без строки запроса (`/landing2/ava-daria.png?v=…` → `/landing2/ava-daria.png`). */
const bare = (url: string): string => url.split('?')[0] ?? url;
const ASSET_BY_PATH = new Map(STOCK_ASSET_FILES.map((a) => [a.path, a.id]));

/** Адрес картинки сцены → ссылка сценария (id ресурса, кружок, знак или картинка человека). */
export function sceneImageRef(url: string | null | undefined, origin?: string): SceneImageRef {
  if (url === undefined || url === null) return null;
  const s = url.trim();
  if (s === '') return '';
  const id = ASSET_BY_PATH.get(bare(s));
  if (id) return id;
  const full = origin && s.startsWith('/') && !s.startsWith('//') ? `${origin.replace(/\/$/, '')}${s}` : s;
  return { url: full, remote: true };
}

/** Слова вокруг переписки (те же ключи, что берёт `Scene`). */
export function sceneUi(t: SceneT, def?: SceneDef | null): SceneUi {
  return {
    hintSend: t('hint.send'),
    hintTap: t('hint.tap'),
    hintTapNamed: t('hint.tapNamed', { btn: '{btn}' }),
    hintWay: t('hint.way'),
    again: t('hint.again'),
    igField: t('igField'),
    cmField: t('s2.field'),
    tgField: t('tgField'),
    cmHeader: t('s2.hdr'),
    igToday: t('igToday'),
    profStart: t('prof.start'),
    tgBotName: t('s3.botName'),
    tgBotSub: t('s3.botSub'),
    tgStartBtn: def?.tgAbout?.btn ?? t('s3.startBtn'),
    exampleBadge: t('exampleBadge'),
  };
}

/**
 * Готовая копия сцены (`sceneDefFor`) → сценарий для приложения. Подстановки (ниша, лицо, пол, слова бота) уже сделаны;
 * здесь только сведение к данным: запасные значения `Scene` раскрыты, картинки — ссылками на ресурсы.
 */
export function sceneScriptOf(def: SceneDef, o: { locale: 'ru' | 'en'; t: SceneT; origin?: string }): SceneScript {
  const { t } = o;
  const img = (u: string | null | undefined) => sceneImageRef(u, o.origin);
  // Лицо владельца бота — как `selfFace` в Scene. ПРАВИЛО ЛИЦА (QUICK-START §10.2 п.2, владелец 29.10.2026): у вошедшего
  // `selfAva` задан ВСЕГДА (personalizeScene: аватарка Инстаграма, нет — '' = фирменный кружок), и стоковое лицо Дарьи
  // (`ava: 'marina'`, ресурс `ava.marina`) ему не подставляется никогда. Дарья — только без профиля человека: ступени
  // «ниша»/«случайная» и статьи справки; сцена нашего аккаунта без лица — знак.
  const self = def.selfAva !== undefined ? img(def.selfAva) : def.ava === 'marina' ? img(AVA.marina) : null;
  // Комментатор под постом — `faceName(1)`/`faceAva(1)` в Scene: из аудитории клиента, иначе свой у сцены.
  const face = def.selfFaces?.[1];
  const commenter = {
    name: face?.name?.trim() || t('s2.c2n'),
    img: face ? img(face.avatar?.trim() || '') : img(AVA.maks),
  };
  const tgAbout = def.tgAbout ?? (def.tgStart ? { h: t('s3.aboutH'), p: t('s3.about'), btn: t('s3.startBtn') } : null);
  return {
    format: SCENE_SCRIPT_FORMAT,
    key: def.k,
    locale: o.locale,
    chat: def.chat,
    own: def.own === true,
    comments: def.comments === true,
    tgStart: def.tgStart === true,
    name: def.name ?? (def.chat === 'tg' ? t('s3.botName') : (def.nick ?? '')),
    ...(def.tgName ? { tgName: def.tgName } : {}),
    nick: def.nick ?? null,
    botWho: def.botWho ?? null,
    faces: {
      self,
      tg: img(def.tgAva ?? SCENE_BOT_AVA),
      post: img(def.postImg ?? SCENE_POST_IMG),
      commenter,
    },
    tgAbout,
    steps: def.steps,
    ui: sceneUi(t, def),
    ...(def.series ? { series: def.series } : {}),
  };
}

/** Сценарий одной сцены: определение → копия под нишу/лицо/слова (`sceneDefFor`) → данные (`sceneScriptOf`). */
export function buildSceneScript(o: {
  key: WizardScene;
  locale: 'ru' | 'en';
  /** Перевод блока `auth.onboarding` языка `locale`. */
  t: SceneT;
  nicheTexts?: SceneTexts | null;
  nicheKey?: string | null;
  texts?: SceneTexts | null;
  profile?: PersonalProfile | null;
  /** Адрес сайта — картинки человека уходят полными адресами. */
  origin?: string;
  /** Готовые определения (`sceneDefs(t)`), чтобы не строить их на каждую сцену пакета. */
  defs?: readonly SceneDef[];
  /** §8.22: сообщения цепочки — свои у бота (правка анкеты): выборки набора нет, шаги как есть. */
  ownLetters?: boolean;
  /** QUICK-START §9.3: лицо у сцен цели и оффер для набора серии (см. `sceneDefFor`). */
  face?: boolean;
  seriesCtx?: Partial<QsRenderCtx>;
}): SceneScript | null {
  const defs = o.defs ?? sceneDefs(o.t, o.locale);
  const base = defs.find((d) => d.k === o.key) ?? null;
  const def = sceneDefFor({
    base,
    key: o.key,
    nicheTexts: o.nicheTexts ?? null,
    nicheKey: o.nicheKey ?? null,
    texts: o.texts ?? null,
    profile: o.profile ?? null,
    locale: o.locale,
    ...(o.face ? { face: true } : {}),
    ...(o.seriesCtx ? { seriesCtx: o.seriesCtx } : {}),
  });
  if (!def) return null;
  return sceneScriptOf(o.ownLetters ? withoutSeries(def) : def, { locale: o.locale, t: o.t, ...(o.origin ? { origin: o.origin } : {}) });
}

/** Пакет сцен одного языка (§3.1) — ответ GET /api/app/scenes и снимок в сборке приложения. */
export interface ScenePack {
  format: number;
  version: string;
  locale: 'ru' | 'en';
  timing: typeof SCENE_TIMING;
  assets: SceneAsset[];
  /** Сцены без ниши и без лица. */
  stock: Record<NativeSceneKey, SceneScript>;
  /** Гостевые варианты сцен тура по нишам: `tour.g1[<ниша>]`. */
  tour: Record<(typeof TOUR_SCENES)[number], Record<string, SceneScript>>;
  /** Правило пары ниш тура: при каждом открытии — g1 случайная, g2 случайная из остальных (§4.1). */
  guest: { pairRule: 'differ' };
}

/** Ниша гостя тура: ключ и её слова для сцены (`nichePhrases(tPers, nicheView, null, <сцена>)`). */
export interface SceneTourNiche {
  key: string;
  /** Слова ниши под сцену тура; функция — потому что у g1 и g2 фразы свои. */
  texts: (scene: (typeof TOUR_SCENES)[number]) => SceneTexts;
}

/** Пакет сцен языка. Чистая: справочник ниш передаёт вызывающий (ручка — из базы, тест — снимок). */
export function buildScenePack(o: { locale: 'ru' | 'en'; t: SceneT; niches: readonly SceneTourNiche[] }): ScenePack {
  const defs = sceneDefs(o.t, o.locale);
  const one = (key: NativeSceneKey, extra: { nicheTexts?: SceneTexts; nicheKey?: string } = {}): SceneScript => {
    const s = buildSceneScript({ key, locale: o.locale, t: o.t, defs, ...extra });
    if (!s) throw new Error(`scene-script: нет сцены ${key}`);
    return s;
  };
  const stock = Object.fromEntries(NATIVE_SCENES.map((k) => [k, one(k)])) as Record<NativeSceneKey, SceneScript>;
  const tour = Object.fromEntries(
    TOUR_SCENES.map((k) => [
      k,
      Object.fromEntries(o.niches.map((n) => [n.key, one(k, { nicheTexts: n.texts(k), nicheKey: n.key })])),
    ]),
  ) as ScenePack['tour'];
  return {
    format: SCENE_SCRIPT_FORMAT,
    version: ASSET_VERSION,
    locale: o.locale,
    timing: SCENE_TIMING,
    assets: sceneAssets(),
    stock,
    tour,
    guest: { pairRule: 'differ' },
  };
}
